import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Package, 
  Clock, 
  CheckCircle, 
  Truck, 
  XCircle,
  ChevronRight,
  MapPin,
  Phone,
  Receipt,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

// Mock orders data
const getMockOrders = (lang) => [
  {
    id: 'PH123456',
    status: 'delivered',
    date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    items: [
      { name: lang === 'ar' ? 'بانادول' : 'Panadol', quantity: 2, price: 5.99 },
      { name: lang === 'ar' ? 'فيتامين سي' : 'Vitamin C', quantity: 1, price: 8.99 },
    ],
    total: 20.97,
    pharmacy: lang === 'ar' ? 'صيدلية الشفاء' : 'Al-Shifa Pharmacy',
    deliveryAddress: lang === 'ar' ? 'شارع المزة، دمشق' : 'Mazzeh Street, Damascus',
  },
  {
    id: 'PH123457',
    status: 'on_the_way',
    date: new Date(Date.now() - 1000 * 60 * 30),
    items: [
      { name: lang === 'ar' ? 'أموكسيسيلين' : 'Amoxicillin', quantity: 1, price: 12.99 },
    ],
    total: 17.99,
    pharmacy: lang === 'ar' ? 'صيدلية الحياة' : 'Al-Hayat Pharmacy',
    deliveryAddress: lang === 'ar' ? 'شارع بغداد، دمشق' : 'Baghdad Street, Damascus',
    estimatedArrival: new Date(Date.now() + 1000 * 60 * 15),
  },
  {
    id: 'PH123458',
    status: 'preparing',
    date: new Date(Date.now() - 1000 * 60 * 10),
    items: [
      { name: lang === 'ar' ? 'سيتريزين' : 'Cetirizine', quantity: 1, price: 6.99 },
      { name: lang === 'ar' ? 'إيبوبروفين' : 'Ibuprofen', quantity: 2, price: 7.99 },
    ],
    total: 27.97,
    pharmacy: lang === 'ar' ? 'صيدلية النور' : 'Al-Nour Pharmacy',
    deliveryAddress: lang === 'ar' ? 'شارع الحمراء، دمشق' : 'Hamra Street, Damascus',
  },
  {
    id: 'PH123459',
    status: 'cancelled',
    date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
    items: [
      { name: lang === 'ar' ? 'ميتفورمين' : 'Metformin', quantity: 1, price: 9.99 },
    ],
    total: 14.99,
    pharmacy: lang === 'ar' ? 'صيدلية الأمل' : 'Al-Amal Pharmacy',
    deliveryAddress: lang === 'ar' ? 'شارع الثورة، دمشق' : 'Thawra Street, Damascus',
    cancelReason: lang === 'ar' ? 'طلب العميل الإلغاء' : 'Customer requested cancellation',
  },
];

const statusConfig = {
  preparing: {
    icon: Package,
    color: 'bg-blue-100 text-blue-700',
    label: { en: 'Preparing', ar: 'قيد التحضير' },
  },
  on_the_way: {
    icon: Truck,
    color: 'bg-amber-100 text-amber-700',
    label: { en: 'On the way', ar: 'في الطريق' },
  },
  delivered: {
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700',
    label: { en: 'Delivered', ar: 'تم التوصيل' },
  },
  cancelled: {
    icon: XCircle,
    color: 'bg-red-100 text-red-700',
    label: { en: 'Cancelled', ar: 'ملغي' },
  },
};

const PharmacyOrdersPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    setTimeout(() => {
      setOrders(getMockOrders(lang));
      setLoading(false);
    }, 500);
  }, [lang]);

  const activeOrders = orders.filter(o => ['preparing', 'on_the_way'].includes(o.status));
  const completedOrders = orders.filter(o => ['delivered', 'cancelled'].includes(o.status));

  const OrderCard = ({ order }) => {
    const config = statusConfig[order.status];
    const StatusIcon = config.icon;

    return (
      <Card className="dark:bg-slate-800 border-0 shadow-sm mb-4" data-testid={`order-${order.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">#{order.id}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {format(order.date, 'PPp', { locale })}
              </p>
            </div>
            <Badge className={config.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {lang === 'ar' ? config.label.ar : config.label.en}
            </Badge>
          </div>

          {order.status === 'on_the_way' && order.estimatedArrival && (
            <div className="p-3 bg-amber-50 rounded-lg mb-3">
              <div className="flex items-center gap-2 text-amber-700">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {lang === 'ar' ? 'الوصول المتوقع:' : 'Estimated arrival:'}{' '}
                  {format(order.estimatedArrival, 'p', { locale })}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2 mb-3">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">{item.name} x{item.quantity}</span>
                <span className="text-slate-900 dark:text-white">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Package className="h-4 w-4" />
              <span>{order.pharmacy}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <MapPin className="h-4 w-4" />
              <span>{order.deliveryAddress}</span>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-3 border-t">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الإجمالي' : 'Total'}</p>
              <p className="font-bold text-green-600">${order.total.toFixed(2)}</p>
            </div>
            {order.status === 'delivered' && (
              <Button variant="outline" size="sm" onClick={() => navigate('/pharmacy/medicines')}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'إعادة الطلب' : 'Reorder'}
              </Button>
            )}
          </div>

          {order.status === 'cancelled' && order.cancelReason && (
            <div className="mt-3 p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>{lang === 'ar' ? 'سبب الإلغاء:' : 'Cancel reason:'}</strong> {order.cancelReason}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="pharmacy-orders-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-3">
            <Receipt className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">
                {lang === 'ar' ? 'طلباتي' : 'My Orders'}
              </h1>
              <p className="text-green-100">
                {lang === 'ar' ? 'تتبع طلبات الأدوية الخاصة بك' : 'Track your medicine orders'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="active" data-testid="active-orders-tab">
              {lang === 'ar' ? 'الطلبات النشطة' : 'Active Orders'}
              {activeOrders.length > 0 && (
                <Badge className="ml-2 bg-green-600">{activeOrders.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="order-history-tab">
              {lang === 'ar' ? 'السجل' : 'History'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                    <CardContent className="p-4">
                      <Skeleton className="h-6 w-32 mb-2" />
                      <Skeleton className="h-4 w-48 mb-4" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : activeOrders.length === 0 ? (
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-12 text-center">
                  <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">
                    {lang === 'ar' ? 'لا توجد طلبات نشطة' : 'No active orders'}
                  </h2>
                  <p className="text-slate-500 mb-6">
                    {lang === 'ar' ? 'طلباتك النشطة ستظهر هنا' : 'Your active orders will appear here'}
                  </p>
                  <Button className="bg-green-600 hover:bg-green-700" onClick={() => navigate('/pharmacy')}>
                    {lang === 'ar' ? 'تصفح الصيدليات' : 'Browse Pharmacies'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              activeOrders.map(order => <OrderCard key={order.id} order={order} />)
            )}
          </TabsContent>

          <TabsContent value="history">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                    <CardContent className="p-4">
                      <Skeleton className="h-6 w-32 mb-2" />
                      <Skeleton className="h-4 w-48 mb-4" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : completedOrders.length === 0 ? (
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-12 text-center">
                  <Clock className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">
                    {lang === 'ar' ? 'لا يوجد سجل' : 'No order history'}
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'طلباتك السابقة ستظهر هنا' : 'Your past orders will appear here'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              completedOrders.map(order => <OrderCard key={order.id} order={order} />)
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PharmacyOrdersPage;
